import React from "react";
import { Info } from "lucide-react";
import { EmptyState, SkeletonLoader, Text, Tooltip } from "@merge-api/merge-javascript-shared";
import { LinkedAccount } from "../../../../../../models/Entities";
import LinkedAccountRow from "./components/LinkedAccountRow/LinkedAccountRow";
import useAppContext from "../../../../../context/useAppContext";
import { MergeFlagFeature, useMergeFlag } from "../../../../../shared/hooks/useMergeFlag";

interface Props {
  linkedAccounts?: LinkedAccount[];
  isLoading: boolean;
  permissionDenied?: boolean;
  hasError?: boolean;
  onDeleteClick: (linkedAccount: LinkedAccount) => void;
}

const COLUMNS = ["Organization", "Integration", "Health", "Date linked"];

const LinkedAccountsTable = ({
  linkedAccounts,
  isLoading,
  onDeleteClick,
  permissionDenied,
  hasError,
}: Props) => {
  const { user } = useAppContext();
  const { enabled, hasInitialized } = useMergeFlag({
    feature: MergeFlagFeature.MERGE_FLAG_LINKED_ACCOUNTS_DATA_FRESH_AS_OF,
    organizationId: user.organization.id,
  });
  const showDataFreshAsOf = enabled === true;
  const columns = showDataFreshAsOf ? [...COLUMNS, "Data fresh as of"] : COLUMNS;
  const columnCount = columns.length + 1;

  if (permissionDenied) {
    return (
      <EmptyState
        title="You do not have permission to access Linked Accounts"
        subtitle="Please contact your organization's administrator to be added as an Admin or Developer to view Linked Accounts"
      />
    );
  }
  if (hasError && !isLoading) {
    return <EmptyState title="Unable to load Linked Accounts" subtitle="Please try again." />;
  }

  return (
    <div className="overflow-x-auto" aria-busy={isLoading}>
      <table className="w-full min-w-[900px] table-fixed text-sm">
        <colgroup>
          <col className={showDataFreshAsOf ? "w-4/12" : "w-5/12"} />
          <col className="w-2/12" />
          <col className="w-2/12" />
          <col className="w-1/12" />
          {showDataFreshAsOf && <col className="w-2/12" />}
          <col className="w-1/12" />
        </colgroup>
        <thead className="border-b border-gray-10">
          <tr>
            {!hasInitialized ? (
              <th colSpan={columnCount} className="px-4 py-2">
                <SkeletonLoader height={20} borderRadius={8} fullWidth />
              </th>
            ) : (
              <>
                {columns.map((column) => (
                  <th key={column} scope="col" className="px-4 py-2 text-left align-middle">
                    <div className="flex items-center gap-1">
                      <Text variant="pre-title" className="whitespace-nowrap">
                        {column}
                      </Text>
                      {column === "Data fresh as of" && (
                        <Tooltip
                          className="flex items-center"
                          title={
                            <div className="flex flex-col gap-2">
                              <span>
                                The oldest freshness timestamp across all enabled Common Models.
                              </span>
                              <span>
                                If any model has never synced successfully, the Linked Account shows
                                –. Its initial sync may still be running, or the model may be
                                failing while others sync successfully.
                              </span>
                              <span>
                                Check the Linked Account’s Sync status to see which model is
                                affected.
                              </span>
                            </div>
                          }
                        >
                          <span
                            tabIndex={0}
                            aria-label="About data freshness"
                            className="inline-flex items-center text-black"
                          >
                            <Info size={12} />
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </th>
                ))}
                <th scope="col" className="px-4 py-2 text-left align-middle" />
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {isLoading || !hasInitialized || !linkedAccounts ? (
            Array.from({ length: 10 }).map((_, index) => (
              <tr key={`linked-account-skeleton-${index}`} className="border-t border-gray-10">
                <td colSpan={columnCount} className="px-4 py-2 align-middle">
                  <SkeletonLoader height={50} borderRadius={8} fullWidth />
                </td>
              </tr>
            ))
          ) : linkedAccounts.length ? (
            linkedAccounts.map((linkedAccount) => (
              <LinkedAccountRow
                key={linkedAccount.id}
                linkedAccount={linkedAccount}
                onDeleteClick={onDeleteClick}
                showDataFreshAsOf={showDataFreshAsOf}
              />
            ))
          ) : (
            <tr className="border-t border-gray-10">
              <td colSpan={columnCount} className="px-4 py-20">
                <EmptyState
                  title="No Linked Accounts"
                  className="flex w-full flex-col items-center justify-center p-0"
                />
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default LinkedAccountsTable;
